<script setup lang="ts">
import type { SearchResultFragment } from '#graphql-operations'
import { UPSELL_FACET_VALUE_ID } from '@layers/common/app/constants/sort'

const open = defineModel<boolean>('open', {
  default: false,
  type: Boolean,
})

const router = useRouter()
const { t } = useI18n()
const { ecommerce } = useAnalytics()

const { data: upsellProducts, pending } = await useAsyncData(`upsell-products-${UPSELL_FACET_VALUE_ID}`, () => useGraphqlQuery('search', {
  input: {
    facetValueFilters: [{ and: UPSELL_FACET_VALUE_ID, byProduct: true }],
    sort: { sales: 'DESC' },
    take: 12,
    skip: 0,
  },
}), {
  transform: result => result?.data?.search?.items || [],
})

function handleViewCart() {
  open.value = false
  router.push('/cart')
}

function handleCheckout() {
  open.value = false
  router.push('/checkout')
}

function handleProductClick(product: SearchResultFragment) {
  open.value = false
  ecommerce.selectItem(product)
}
</script>

<template>
  <UiDrawer v-model:open="open">
    <UiDrawerContent>
      <!-- Success indicator and title -->
      <div class="flex flex-col items-center pb-4 pt-6">
        <div class="bg-success-100 mb-3 h-12 w-12 flex items-center justify-center rounded-full">
          <Icon name="i-heroicons:check" class="h-6 w-6 text-success" />
        </div>
        <UiDrawerTitle class="text-center">
          {{ t('cart.item_added_to_cart') }}
        </UiDrawerTitle>
        <p class="mt-1 text-sm text-muted-foreground">
          {{ t('cart.continue_shopping_or_checkout') }}
        </p>
      </div>

      <div class="pb-6 space-y-6">
        <!-- Action buttons -->
        <div class="grid grid-cols-2 gap-3 px-4">
          <UiButton
            variant="outline"
            size="lg"
            class="w-full"
            @click="handleViewCart"
          >
            <Icon name="i-heroicons:shopping-bag" class="mr-2 h-4 w-4" />
            {{ t('checkout.goto_cart.label') }}
          </UiButton>
          <UiButton
            size="lg"
            variant="success"
            class="w-full"
            @click="handleCheckout"
          >
            {{ t('checkout.label') }}
            <Icon name="i-heroicons:arrow-right" class="ml-2 h-4 w-4" />
          </UiButton>
        </div>

        <!-- Upsell products section -->
        <div v-if="upsellProducts && upsellProducts.length > 0" class="space-y-4">
          <div class="px-4">
            <h3 class="text-base font-semibold">
              {{ t('product.recommended_for_you') }}
            </h3>
            <p class="text-sm text-muted-foreground">
              {{ t('product.customers_also_bought') }}
            </p>
          </div>

          <!-- Product carousel -->
          <div class="relative px-4">
            <UiCarousel class="w-full">
              <UiCarouselContent class="-ml-2">
                <UiCarouselItem
                  v-for="product in upsellProducts"
                  :key="product.id"
                  class="basis-[45%] pl-2"
                >
                  <ProductCard
                    :item="product"
                    @click="handleProductClick(product)"
                  />
                </UiCarouselItem>
              </UiCarouselContent>
              <UiCarouselPrevious class="absolute left-0 top-1/2 -translate-x-1/2" />
              <UiCarouselNext class="absolute right-0 top-1/2 translate-x-1/2" />
            </UiCarousel>
          </div>
        </div>

        <!-- Loading state -->
        <div v-else-if="pending" class="space-y-4">
          <div class="px-4 space-y-2">
            <UiSkeleton class="h-5 w-48" />
            <UiSkeleton class="h-4 w-32" />
          </div>
          <div class="px-4">
            <div class="grid grid-cols-2 gap-2">
              <ProductCardSkeleton v-for="i in 2" :key="i" />
            </div>
          </div>
        </div>
      </div>
    </UiDrawerContent>
  </UiDrawer>
</template>
